import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "../../contexts/LanguageContext";
import { useStore } from "../../contexts/StoreContext";
import {
  fetchIntegration,
  fetchIntegrationAudiences,
  fetchPasses,
  fetchWebhookEvents,
} from "../../api/queries";
import { updateIntegration } from "../../api/mutations";
import { WEBHOOK_EVENT_OPTIONS } from "../../constants/webhookEvents";
import {
  PuzzlePieceIcon,
  EyeIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import Header from "../../components/Header";
import TagInput from "../../components/TagInput";
import {
  generateClientId,
  generateClientSecret,
} from "../../utils/credentials";

const IntegrationConfigPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { selectedStore } = useStore();
  const storeId = selectedStore?.id;

  // Form state
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [api_url, setApi_url] = useState("");
  const [shopDomain, setShopDomain] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [passId, setPassId] = useState("");
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [enableCustomerIntegration, setEnableCustomerIntegration] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [integrationType, setIntegrationType] = useState(null);
  const [integrationName, setIntegrationName] = useState("");
  const [selectedAudience, setSelectedAudience] = useState("");
  const [sendCustomerEnabled, setSendCustomerEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookAuthType, setWebhookAuthType] = useState("none");
  const [webhookAuthToken, setWebhookAuthToken] = useState("");
  const [selectedWebhookEvent, setSelectedWebhookEvent] = useState(null);

  // BookingKit state
  const [bkEnabled, setBkEnabled] = useState(true);
  const [bkOauthClientId, setBkOauthClientId] = useState("");
  const [bkOauthClientSecret, setBkOauthClientSecret] = useState("");
  const [showBkOauthSecret, setShowBkOauthSecret] = useState(false);
  const [bkAllowedVendorIds, setBkAllowedVendorIds] = useState([]);
  const [bkAllowedProductIds, setBkAllowedProductIds] = useState([]);
  const [bkEligibleStatuses, setBkEligibleStatuses] = useState(["ACCEPTED"]);
  const [bkExpectedCurrency, setBkExpectedCurrency] = useState("EUR");
  const [bkClaimGraceDays, setBkClaimGraceDays] = useState(7);

  // Add state to track original values
  const [originalValues, setOriginalValues] = useState({
    accountSid: "",
    authToken: "",
    apiKey: "",
    apiUrl: "",
    shopDomain: "",
    webhookSecret: "",
    passId: "",
    enableCustomerIntegration: false,
    selectedAudience: "",
    sendCustomerEnabled: false,
    webhookUrl: "",
    webhookEvents: [],
    webhookAuthType: "none",
    webhookAuthToken: "",
  });

  // Fetch integration data
  const { data: integration, isLoading: isIntegrationLoading } = useQuery({
    queryKey: ["integration", storeId, id],
    queryFn: () => fetchIntegration(storeId, id),
    enabled: !!storeId && !!id,
  });

  // Fetch Mailchimp audiences if this is a Mailchimp integration
  const { data: audiences = [], isLoading: isAudiencesLoading } = useQuery({
    queryKey: ["audiences", storeId, id],
    queryFn: () => fetchIntegrationAudiences(storeId, id),
    enabled:
      !!storeId &&
      !!id &&
      (integration?.integration_type === "mailchimp" ||
        integration?.integration_type === "brevo" ||
        integrationType === 3 ||
        integrationType === 10),
  });

  // Fetch loyalty passes
  const { data: passes = [], isLoading: isPassesLoading } = useQuery({
    queryKey: ["passes", storeId],
    queryFn: () => fetchPasses(storeId),
    enabled: !!storeId,
  });

  // Fetch webhook events log
  const { data: webhookEventLogs = [] } = useQuery({
    queryKey: ["webhookEvents", storeId, id],
    queryFn: () => fetchWebhookEvents(storeId, id),
    enabled: !!storeId && !!id && integrationType === 12,
    refetchInterval: 10000,
  });

  // Effect to handle integration data update
  useEffect(() => {
    if (integration) {
      // Set type - try both from data structure and from direct type
      const integrationTypeString =
        integration.integration_type || integration.type;

      if (integrationTypeString) {
        const type = getIntegrationType(integrationTypeString);
        setIntegrationType(type);
        setIntegrationName(getIntegrationName(integrationTypeString));
      }

      // Set form values based on config
      const config = integration.config || {};

      if (integrationTypeString === "twilio") {
        setAccountSid(config.accountSid || "");
        setAuthToken(config.authToken || "");
        // Save original values
        setOriginalValues((prev) => ({
          ...prev,
          accountSid: config.accountSid || "",
          authToken: config.authToken || "",
        }));
      } else if (
        ["sendgrid", "mailchimp", "mandrill", "brevo"].includes(
          integrationTypeString,
        )
      ) {
        setApiKey(config.apiKey || "");
        // Save original values
        setOriginalValues((prev) => ({
          ...prev,
          apiKey: config.apiKey || "",
        }));

        if (["mailchimp", "brevo"].includes(integrationTypeString)) {
          if (config.listId) {
            setSelectedAudience(config.listId);
            // Save original audience
            setOriginalValues((prev) => ({
              ...prev,
              selectedAudience: config.listId,
            }));
          }

          // Set send customer enabled state - using top-level property
          const enabled = integration.send_customer_enabled || false;
          setSendCustomerEnabled(enabled);
          setOriginalValues((prev) => ({
            ...prev,
            sendCustomerEnabled: enabled,
          }));
        }
      } else if (integrationTypeString === "mailgun") {
        setApiKey(config.apiKey || "");
        setApi_url(config.apiUrl || "");
        // Save original values
        setOriginalValues((prev) => ({
          ...prev,
          apiKey: config.apiKey || "",
          apiUrl: config.apiUrl || "",
        }));
      } else if (integrationTypeString === "shopify") {
        setShopDomain(config.shopDomain || "");
        setWebhookSecret(config.webhookSecret || "");
        setPassId(config.passId || "");

        // Set customer integration enabled state - using top-level property
        const enabled = integration.customer_integration_enabled || false;
        setEnableCustomerIntegration(enabled);

        // Save original values
        setOriginalValues((prev) => ({
          ...prev,
          shopDomain: config.shopDomain || "",
          webhookSecret: config.webhookSecret || "",
          passId: config.passId || "",
          enableCustomerIntegration: enabled,
        }));
      } else if (integrationTypeString === "bookingkit") {
        setPassId(config.passId || "");
        setBkEnabled(config.enabled !== false);
        // Auto-generate credentials if missing (legacy integrations
        // created before auto-fill was introduced). The generated
        // values will be persisted on next Save.
        setBkOauthClientId(config.oauthClientId || generateClientId());
        setBkOauthClientSecret(config.oauthClientSecret || generateClientSecret());
        setBkAllowedVendorIds(config.allowedVendorIds || []);
        setBkAllowedProductIds(config.allowedProductIds || []);
        setBkEligibleStatuses(config.eligibleStatuses || ["ACCEPTED"]);
        setBkExpectedCurrency(config.expectedCurrency || "EUR");
        setBkClaimGraceDays(
          Number.isFinite(config.claimGracePeriodDays) && config.claimGracePeriodDays > 0
            ? config.claimGracePeriodDays
            : 7,
        );

        setOriginalValues((prev) => ({
          ...prev,
          passId: config.passId || "",
          bkEnabled: config.enabled !== false,
          bkOauthClientId: config.oauthClientId || "",
          bkOauthClientSecret: config.oauthClientSecret || "",
          bkAllowedVendorIds: config.allowedVendorIds || [],
          bkAllowedProductIds: config.allowedProductIds || [],
          bkEligibleStatuses: config.eligibleStatuses || ["ACCEPTED"],
          bkExpectedCurrency: config.expectedCurrency || "EUR",
          bkClaimGraceDays:
            Number.isFinite(config.claimGracePeriodDays) && config.claimGracePeriodDays > 0
              ? config.claimGracePeriodDays
              : 7,
        }));
      } else if (integrationTypeString === "webhook") {
        setWebhookUrl(config.url || "");
        setWebhookEvents(config.events || []);
        setWebhookAuthType(config.auth_type || "none");
        setWebhookAuthToken(config.auth_token || "");

        setOriginalValues((prev) => ({
          ...prev,
          webhookUrl: config.url || "",
          webhookEvents: config.events || [],
          webhookAuthType: config.auth_type || "none",
          webhookAuthToken: config.auth_token || "",
        }));
      }
    }
  }, [integration]);

  // Effect to handle audiences data
  useEffect(() => {
    // If we have audiences data and listName but no selectedAudience yet
    if (
      audiences?.audiences?.length > 0 &&
      integration?.config?.listName &&
      !selectedAudience
    ) {
      // Try to find the audience by name
      const audience = audiences.audiences.find(
        (a) => a.name === integration.config.listName,
      );

      if (audience) {
        setSelectedAudience(audience.id);
      }
    }
  }, [audiences, integration, selectedAudience]);

  // Helper to get numeric integration type from string
  const getIntegrationType = (typeString) => {
    if (!typeString) return null;

    // Handle both string format variations and case insensitivity
    const normalizedType = typeString.toLowerCase();

    const typeMap = {
      twilio: 1,
      sendgrid: 2,
      mailchimp: 3,
      mailgun: 4,
      mandrill: 11,
      brevo: 10,
      shopify: 7,
      webhook: 12,
      bookingkit: 13,
    };
    return typeMap[normalizedType] || null;
  };

  // Helper to get integration name
  const getIntegrationName = (typeString) => {
    const nameMap = {
      twilio: "Twilio",
      sendgrid: "SendGrid",
      mailchimp: "Mailchimp",
      mailgun: "Mailgun",
      mandrill: "Mandrill",
      brevo: "Brevo",
      shopify: "Shopify",
      webhook: "Webhook",
      bookingkit: "BookingKit",
    };
    return nameMap[typeString] || "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage(""); // Clear any previous errors

      let integrationPayload = {
        integration_type: integrationType,
        config: {},
        tags: integration.tags || [], // Preserve existing tags
      };

      // Configure based on integration type
      switch (integrationType) {
        case 1: // Twilio
          integrationPayload.config = {
            accountSid: accountSid,
            authToken: authToken,
          };
          break;
        case 2: // Sendgrid
          integrationPayload.config = {
            apiKey: apiKey,
          };
          break;
        case 3: // Mailchimp
          integrationPayload.config = {
            apiKey: apiKey,
            listId: selectedAudience,
          };
          integrationPayload.send_customer_enabled = sendCustomerEnabled;
          break;
        case 4: // Mailgun
          integrationPayload.config = {
            apiKey: apiKey,
            apiUrl: api_url,
          };
          break;
        case 7: // Shopify
          integrationPayload.config = {
            ...integration.config, // Preserve all existing config values
            shopDomain: shopDomain,
            webhookSecret: webhookSecret,
            passId: passId,
          };
          integrationPayload.customer_integration_enabled =
            enableCustomerIntegration;
          break;
        case 11: // Mandrill
          integrationPayload.config = {
            apiKey: apiKey,
          };
          break;
        case 10: // Brevo
          integrationPayload.config = {
            apiKey: apiKey,
            listId: selectedAudience,
          };
          integrationPayload.send_customer_enabled = sendCustomerEnabled;
          break;
        case 12: // Webhook
          integrationPayload.config = {
            url: webhookUrl,
            events: webhookEvents,
            auth_type: webhookAuthType,
            ...(webhookAuthType === "bearer" && { auth_token: webhookAuthToken }),
          };
          break;
        case 13: // BookingKit
          integrationPayload.config = {
            ...integration.config,
            enabled: bkEnabled,
            passId: passId,
            oauthClientId: bkOauthClientId,
            oauthClientSecret: bkOauthClientSecret,
            allowedVendorIds: bkAllowedVendorIds,
            allowedProductIds: bkAllowedProductIds,
            eligibleStatuses: bkEligibleStatuses,
            expectedCurrency: bkExpectedCurrency,
            claimGracePeriodDays: Number(bkClaimGraceDays) || 7,
          };
          break;
      }

      await updateIntegration({
        storeId: storeId,
        integrationId: integration.id,
        integrationData: integrationPayload,
      });

      // Navigate back to integrations page
      navigate("/integrations");
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to update integration. Please check your credentials and try again.",
      );
      setIsLoading(false);
    }
  };

  // Get form fields based on integration type
  const getFormFields = () => {
    // Define integration-specific fields
    const integrationFields = {
      1: [
        // Twilio
        {
          id: "accountSid",
          label: "Account SID",
          type: "text",
          value: accountSid,
          onChange: (e) => setAccountSid(e.target.value),
          placeholder: "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
          required: true,
          isSecret: false,
        },
        {
          id: "authToken",
          label: "Auth Token",
          type: showAuthToken ? "text" : "password",
          value: authToken,
          onChange: (e) => setAuthToken(e.target.value),
          required: true,
          isSecret: true,
        },
      ],
      2: [
        // SendGrid
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: true,
          isSecret: true,
        },
      ],
      3: [
        // Mailchimp
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: true,
          isSecret: true,
        },
      ],
      4: [
        // Mailgun
        {
          id: "api_url",
          label: "Region",
          type: "select",
          value: api_url,
          onChange: (e) => setApi_url(e.target.value),
          required: true,
          isSecret: false,
          options: [
            {
              value: "",
              label: t("integrations.form.selectRegion"),
            },
            {
              value: "api.mailgun.net",
              label: t("integrations.regions.global"),
            },
            {
              value: "api.eu.mailgun.net",
              label: t("integrations.regions.eu"),
            },
          ],
        },
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: true,
          isSecret: true,
        },
      ],
      7: [
        // Shopify
        {
          id: "shopDomain",
          label: "Shop Domain",
          type: "text",
          value: shopDomain,
          onChange: (e) => setShopDomain(e.target.value),
          placeholder: "your-store.myshopify.com",
          required: true,
          isSecret: false,
        },
      ],
      11: [
        // Mandrill
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: true,
          isSecret: true,
        },
      ],
      10: [
        // Brevo
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: true,
          isSecret: true,
        },
      ],
      12: [
        // Webhook
        {
          id: "webhookUrl",
          label: t("integrations.webhook.endpointUrl"),
          type: "text",
          value: webhookUrl,
          onChange: (e) => setWebhookUrl(e.target.value),
          placeholder: "https://example.com/webhook",
          required: true,
          isSecret: false,
        },
      ],
    };

    return integrationFields[integrationType] || [];
  };

  // Check if form has changed
  const hasFormChanged = useMemo(() => {
    if (integrationType === 1) {
      // Twilio
      return (
        accountSid !== originalValues.accountSid ||
        authToken !== originalValues.authToken
      );
    } else if (integrationType === 3 || integrationType === 10) {
      // Mailchimp or Brevo
      return (
        apiKey !== originalValues.apiKey ||
        selectedAudience !== originalValues.selectedAudience ||
        sendCustomerEnabled !== originalValues.sendCustomerEnabled
      );
    } else if (integrationType === 4) {
      // Mailgun
      return (
        apiKey !== originalValues.apiKey || api_url !== originalValues.apiUrl
      );
    } else if (integrationType === 7) {
      // Shopify
      return (
        shopDomain !== originalValues.shopDomain ||
        webhookSecret !== originalValues.webhookSecret ||
        passId !== originalValues.passId ||
        enableCustomerIntegration !== originalValues.enableCustomerIntegration
      );
    } else if (integrationType === 12) {
      // Webhook
      return (
        webhookUrl !== originalValues.webhookUrl ||
        webhookAuthType !== originalValues.webhookAuthType ||
        webhookAuthToken !== originalValues.webhookAuthToken ||
        JSON.stringify(webhookEvents.slice().sort()) !==
          JSON.stringify((originalValues.webhookEvents || []).slice().sort())
      );
    } else if (integrationType === 13) {
      // BookingKit
      const arrChanged = (a, b) =>
        JSON.stringify((a || []).slice().sort()) !==
        JSON.stringify((b || []).slice().sort());
      return (
        passId !== originalValues.passId ||
        bkOauthClientId !== originalValues.bkOauthClientId ||
        bkOauthClientSecret !== originalValues.bkOauthClientSecret ||
        bkEnabled !== originalValues.bkEnabled ||
        bkExpectedCurrency !== originalValues.bkExpectedCurrency ||
        Number(bkClaimGraceDays) !== Number(originalValues.bkClaimGraceDays) ||
        arrChanged(bkAllowedVendorIds, originalValues.bkAllowedVendorIds) ||
        arrChanged(bkAllowedProductIds, originalValues.bkAllowedProductIds) ||
        arrChanged(bkEligibleStatuses, originalValues.bkEligibleStatuses)
      );
    } else {
      // SendGrid, Mandrill, etc.
      return apiKey !== originalValues.apiKey;
    }
  }, [
    integrationType,
    accountSid,
    authToken,
    apiKey,
    api_url,
    shopDomain,
    webhookSecret,
    passId,
    selectedAudience,
    sendCustomerEnabled,
    originalValues,
    enableCustomerIntegration,
    webhookUrl,
    webhookEvents,
    webhookAuthType,
    webhookAuthToken,
    bkEnabled,
    bkOauthClientId,
    bkOauthClientSecret,
    bkAllowedVendorIds,
    bkAllowedProductIds,
    bkEligibleStatuses,
    bkExpectedCurrency,
    bkClaimGraceDays,
  ]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    // Special case for Mailchimp with customer info enabled
    if (integrationType === 3 && sendCustomerEnabled) {
      return selectedAudience && selectedAudience.trim() !== "";
    }
    // For Brevo, always valid when customer sync is enabled
    if (integrationType === 10 && sendCustomerEnabled) {
      return true;
    }
    // For Shopify, webhookSecret is always required, and passId is required if customer integration is enabled
    if (integrationType === 7) {
      return (
        webhookSecret &&
        webhookSecret.trim() !== "" &&
        (!enableCustomerIntegration || (passId && passId.trim() !== ""))
      );
    }
    // For Webhook, URL required and at least one event
    if (integrationType === 12) {
      return (
        webhookUrl &&
        webhookUrl.trim() !== "" &&
        webhookEvents.length > 0
      );
    }
    // For BookingKit: passId, OAuth credentials, at least one vendor id and eligible status
    if (integrationType === 13) {
      return (
        passId &&
        passId.trim() !== "" &&
        bkOauthClientId &&
        bkOauthClientId.trim() !== "" &&
        bkOauthClientSecret &&
        bkOauthClientSecret.trim() !== "" &&
        bkAllowedVendorIds.length > 0 &&
        bkEligibleStatuses.length > 0
      );
    }
    // For other cases, always valid
    return true;
  }, [
    integrationType,
    sendCustomerEnabled,
    selectedAudience,
    passId,
    webhookSecret,
    enableCustomerIntegration,
    webhookUrl,
    webhookEvents,
    bkOauthClientId,
    bkOauthClientSecret,
    bkAllowedVendorIds,
    bkEligibleStatuses,
  ]);

  // Check if this is a Mailchimp or Brevo integration
  const isCustomerSyncEnabled = integrationType === 3 || integrationType === 10;

  if (isIntegrationLoading) {
    return (
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div>{t("common.loading")}</div>
      </div>
    );
  }

  if (!integration) {
    return (
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div>
          No integration data found.{" "}
          <button
            onClick={() => navigate("/integrations")}
            className="text-indigo-600"
          >
            Return to integrations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
      <Header
        icon={PuzzlePieceIcon}
        title={`${integrationName} ${t("integrations.configuration")}`}
        description={t("integrations.form.updateDescription", {
          integrationName,
        })}
      />

      <div className="bg-white p-6 rounded-lg shadow-xs">
        {errorMessage && (
          <div className="mb-4">
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <div className="text-sm text-red-700">
                    <p>{errorMessage}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Connection Configuration Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("integrations.config.connectionConfiguration")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t("integrations.config.connectionDescription", {
                integrationName,
              })}
            </p>

            <div className="space-y-4">
              {getFormFields().map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={field.id}
                    className="block text-sm font-medium text-gray-700"
                  >
                    {field.label}
                  </label>
                  <div className={`${field.isSecret ? "relative" : ""} mt-1`}>
                    {field.type === "select" ? (
                      <select
                        id={field.id}
                        value={field.value}
                        onChange={field.onChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required={field.required}
                      >
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        id={field.id}
                        value={field.value}
                        onChange={field.onChange}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                          field.isSecret ? "pr-10" : ""
                        }`}
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                    )}
                    {field.isSecret && (
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3"
                        onClick={() => {
                          if (field.id === "authToken") {
                            setShowAuthToken(!showAuthToken);
                          } else if (field.id === "apiKey") {
                            setShowApiKey(!showApiKey);
                          }
                        }}
                      >
                        {(field.id === "authToken" && showAuthToken) ||
                        (field.id === "apiKey" && showApiKey) ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-gray-500"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-gray-500"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Webhook Authorization Section */}
          {integrationType === 12 && (
            <div className="bg-white rounded-lg border border-gray-200 p-5 mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("integrations.webhook.authorization")}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {t("integrations.webhook.authDescription")}
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("integrations.webhook.authType")}
                  </label>
                  <select
                    value={webhookAuthType}
                    onChange={(e) => {
                      setWebhookAuthType(e.target.value);
                      if (e.target.value === "none") setWebhookAuthToken("");
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="none">{t("integrations.webhook.authNone")}</option>
                    <option value="bearer">{t("integrations.webhook.authBearer")}</option>
                  </select>
                </div>
                {webhookAuthType === "bearer" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t("integrations.webhook.bearerToken")}
                    </label>
                    <input
                      type="password"
                      value={webhookAuthToken}
                      onChange={(e) => setWebhookAuthToken(e.target.value)}
                      placeholder="your-secret-token"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Webhook Events Section */}
          {integrationType === 12 && (
            <div className="bg-white rounded-lg border border-gray-200 p-5 mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("integrations.webhook.events")}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {t("integrations.webhook.selectEvents")}
              </p>
              <div className="space-y-3">
                {WEBHOOK_EVENT_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center space-x-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={webhookEvents.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setWebhookEvents([...webhookEvents, option.value]);
                        } else {
                          setWebhookEvents(
                            webhookEvents.filter((ev) => ev !== option.value),
                          );
                        }
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">
                      {t(option.labelKey)}
                    </span>
                  </label>
                ))}
              </div>
              {webhookEvents.length === 0 && (
                <p className="mt-2 text-xs text-red-500">
                  {t("integrations.webhook.selectAtLeastOne")}
                </p>
              )}
            </div>
          )}

          {/* Webhook Events Log */}
          {integrationType === 12 && webhookEventLogs.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-5 mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("integrations.webhook.recentEvents")}
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("integrations.webhook.eventName")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("integrations.webhook.statusLabel")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("integrations.webhook.responseCode")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("integrations.webhook.attempts")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("integrations.webhook.date")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {webhookEventLogs.map((event) => (
                      <tr key={event.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                          {event.event}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              event.status === "success"
                                ? "bg-green-100 text-green-800"
                                : event.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {event.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {event.response_code || "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {event.attempts}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Date(event.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                          <button
                            type="button"
                            onClick={() => setSelectedWebhookEvent(event)}
                            className="text-gray-400 hover:text-indigo-600"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Webhook Event Detail Modal */}
          {selectedWebhookEvent && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div
                className="fixed inset-0 bg-black/30"
                onClick={() => setSelectedWebhookEvent(null)}
              />
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
                    <h3 className="text-lg font-medium text-gray-900">
                      <span className="font-mono">{selectedWebhookEvent.event}</span>
                      <span
                        className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          selectedWebhookEvent.status === "success"
                            ? "bg-green-100 text-green-800"
                            : selectedWebhookEvent.status === "failed"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {selectedWebhookEvent.status}
                      </span>
                    </h3>
                    <button
                      type="button"
                      onClick={() => setSelectedWebhookEvent(null)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Request Payload</h4>
                      <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(selectedWebhookEvent.payload, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Request Headers</h4>
                      <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(selectedWebhookEvent.request_headers, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        Response {selectedWebhookEvent.response_code ? `(${selectedWebhookEvent.response_code})` : ""}
                      </h4>
                      <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap">
                        {selectedWebhookEvent.response_body || "-"}
                      </pre>
                    </div>
                    <div className="flex gap-6 text-sm text-gray-500">
                      <span>Attempts: {selectedWebhookEvent.attempts}</span>
                      <span>Created: {new Date(selectedWebhookEvent.created_at).toLocaleString()}</span>
                      {selectedWebhookEvent.completed_at && (
                        <span>Completed: {new Date(selectedWebhookEvent.completed_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Shopify Client Integration Section */}
          {integrationType === 7 && (
            <div className="bg-white rounded-lg border border-gray-200 p-5 mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("integrations.customerIntegration.title")}
                </h3>
                <div className="flex items-center">
                  <div className="relative inline-flex items-center">
                    <label
                      htmlFor="customer-integration-toggle"
                      className="flex cursor-pointer items-center"
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          id="customer-integration-toggle"
                          checked={enableCustomerIntegration}
                          onChange={() =>
                            setEnableCustomerIntegration(
                              !enableCustomerIntegration,
                            )
                          }
                          className="sr-only"
                        />
                        <div
                          className={`h-6 w-11 rounded-full transition-colors duration-200 ease-in-out ${
                            enableCustomerIntegration
                              ? "bg-green-500"
                              : "bg-gray-200"
                          }`}
                        ></div>
                        <div
                          className={`absolute left-0.5 top-0.5 h-5 w-5 transform rounded-full bg-white border border-gray-300 transition-transform duration-200 ease-in-out ${
                            enableCustomerIntegration
                              ? "translate-x-5"
                              : "translate-x-0"
                          }`}
                        ></div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                {t("integrations.customerIntegration.description")}
              </p>

              <div className="space-y-4">
                {/* Always render the fields, but make them required only if integration is enabled */}
                {/* Loyalty Card Selection */}
                <div>
                  <label
                    htmlFor="passId"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("integrations.customerIntegration.selectCard")}
                  </label>
                  <div className="mt-1">
                    <select
                      id="passId"
                      value={passId}
                      onChange={(e) => setPassId(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required={enableCustomerIntegration}
                    >
                      <option value="">Select a loyalty card</option>
                      {isPassesLoading ? (
                        <option disabled>Loading cards...</option>
                      ) : (
                        passes?.map((pass) => (
                          <option key={pass.id} value={pass.id}>
                            {pass.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* Webhook Secret */}
                <div>
                  <label
                    htmlFor="webhookSecret"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("integrations.customerIntegration.webhookSecret")}
                  </label>
                  <div className="relative mt-1">
                    <input
                      type={showWebhookSecret ? "text" : "password"}
                      id="webhookSecret"
                      value={webhookSecret}
                      onChange={(e) => setWebhookSecret(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm pr-10"
                      placeholder="Enter webhook secret"
                      required={enableCustomerIntegration}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                      onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                    >
                      {showWebhookSecret ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-500"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-500"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BookingKit Configuration */}
          {integrationType === 13 && (
            <div className="bg-white rounded-lg border border-gray-200 p-5 mt-6 space-y-5">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  BookingKit Configuration
                </h3>
                <label
                  htmlFor="bk-enabled-toggle"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <span className="text-sm text-gray-700">
                    {bkEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      id="bk-enabled-toggle"
                      checked={bkEnabled}
                      onChange={() => setBkEnabled(!bkEnabled)}
                      className="sr-only"
                    />
                    <div
                      className={`h-6 w-11 rounded-full transition-colors duration-200 ${
                        bkEnabled ? "bg-green-500" : "bg-gray-200"
                      }`}
                    />
                    <div
                      className={`absolute left-0.5 top-0.5 h-5 w-5 transform rounded-full bg-white border border-gray-300 transition-transform duration-200 ${
                        bkEnabled ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Loyalty Card
                </label>
                <select
                  value={passId}
                  onChange={(e) => setPassId(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                >
                  <option value="">Select a loyalty card</option>
                  {isPassesLoading ? (
                    <option disabled>Loading cards...</option>
                  ) : (
                    passes?.map((pass) => (
                      <option key={pass.id} value={pass.id}>
                        {pass.name}
                      </option>
                    ))
                  )}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Customers will earn points on this loyalty card.
                </p>
              </div>

              <div className="rounded-md border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
                <p className="font-medium mb-2">How authentication works</p>
                <p className="text-xs leading-5">
                  BookingKit uses OAuth2 client_credentials. They first
                  POST the credentials below to your <em>token URL</em>{" "}
                  to get a short-lived Bearer token, then PATCH your{" "}
                  <em>webhook URL</em> with that token. The Client ID
                  and Secret are auto-generated for you — share all
                  four values below with BookingKit support to register
                  the webhook.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    OAuth Client ID
                  </label>
                  <button
                    type="button"
                    onClick={() => setBkOauthClientId(generateClientId())}
                    className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-500"
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    Regenerate
                  </button>
                </div>
                <input
                  type="text"
                  value={bkOauthClientId}
                  readOnly
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-xs sm:text-sm font-mono"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    OAuth Client Secret
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setBkOauthClientSecret(generateClientSecret())
                    }
                    className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-500"
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    Regenerate
                  </button>
                </div>
                <div className="relative mt-1">
                  <input
                    type={showBkOauthSecret ? "text" : "password"}
                    value={bkOauthClientSecret}
                    readOnly
                    className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-xs sm:text-sm pr-10 font-mono"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500"
                    onClick={() => setShowBkOauthSecret(!showBkOauthSecret)}
                  >
                    <EyeIcon className="w-5 h-5" />
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Regenerating invalidates previously issued tokens.
                  BookingKit will need the new value to re-authenticate.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Token URL{" "}
                  <span className="font-normal text-gray-500">
                    (oauth_endpoint_token_url)
                  </span>
                </label>
                <input
                  type="text"
                  readOnly
                  value={`${(import.meta.env.VITE_API_URL || "").trim().replace(/\/+$/, "")}/webhooks/bookingkit/token`}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-xs sm:text-sm font-mono"
                />
                <p className="mt-1 text-xs text-gray-500">
                  BookingKit POSTs the Client ID and Secret here to
                  obtain a Bearer token.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Webhook URL{" "}
                  <span className="font-normal text-gray-500">
                    (oauth_endpoint_url)
                  </span>
                </label>
                <input
                  type="text"
                  readOnly
                  value={`${(import.meta.env.VITE_API_URL || "").trim().replace(/\/+$/, "")}/webhooks/bookingkit`}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-xs sm:text-sm font-mono"
                />
                <p className="mt-1 text-xs text-gray-500">
                  BookingKit PATCHes order create/update events to this
                  URL with the Bearer token in the Authorization header.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Allowed Vendor IDs
                </label>
                <div className="mt-1">
                  <TagInput
                    values={bkAllowedVendorIds}
                    onChange={setBkAllowedVendorIds}
                    placeholder="Enter BookingKit vendor ID and press Enter"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Only orders with one of these vendor IDs will be processed.
                  Locations not listed (e.g. Muenster) are ignored.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Allowed Product IDs
                </label>
                <div className="mt-1">
                  <TagInput
                    values={bkAllowedProductIds}
                    onChange={setBkAllowedProductIds}
                    placeholder="Enter BookingKit product/event ID and press Enter"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  If empty, all non-voucher products earn points. Do not add
                  voucher products here.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Eligible Order Statuses
                </label>
                <div className="mt-1">
                  <TagInput
                    values={bkEligibleStatuses}
                    onChange={setBkEligibleStatuses}
                    placeholder="ACCEPTED"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Points are awarded only when the BookingKit order
                  status is in this list. Recommended: ACCEPTED (the
                  default). Adding PENDING awards points before the
                  booking is confirmed &mdash; points are not reversed if a
                  pending booking is later declined. Do not list OPEN,
                  DECLINED or CANCELED here.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Expected Currency
                  </label>
                  <input
                    type="text"
                    value={bkExpectedCurrency}
                    onChange={(e) =>
                      setBkExpectedCurrency(e.target.value.toUpperCase())
                    }
                    placeholder="EUR"
                    maxLength={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm uppercase"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Retro-claim grace period (days)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={bkClaimGraceDays}
                    onChange={(e) =>
                      setBkClaimGraceDays(Number(e.target.value) || 0)
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    When a customer signs up within this window, any earlier
                    pending BookingKit orders for their email are awarded.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Extra Configuration Section - Only for specific integrations */}
          {isCustomerSyncEnabled && (
            <div className="bg-white rounded-lg border border-gray-200 p-5 mt-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-gray-900 mr-3">
                    {t("integrations.sendCustomerInfo.title")}
                  </h3>
                  <div className="relative inline-flex items-center">
                    <label
                      htmlFor="send-customer-toggle"
                      className="flex cursor-pointer items-center"
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          id="send-customer-toggle"
                          checked={sendCustomerEnabled}
                          onChange={() =>
                            setSendCustomerEnabled(!sendCustomerEnabled)
                          }
                          className="sr-only"
                        />
                        <div
                          className={`h-6 w-11 rounded-full transition-colors duration-200 ease-in-out ${sendCustomerEnabled ? "bg-green-500" : "bg-gray-200"}`}
                        ></div>
                        <div
                          className={`absolute left-0.5 top-0.5 h-5 w-5 transform rounded-full bg-white border border-gray-300 transition-transform duration-200 ease-in-out ${
                            sendCustomerEnabled
                              ? "translate-x-5"
                              : "translate-x-0"
                          }`}
                        ></div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                {t("integrations.sendCustomerInfo.description")}
              </p>

              {sendCustomerEnabled && (
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="audienceList"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("integrations.audienceListName")}
                    </label>
                    <div className="mt-1">
                      <select
                        id="audienceList"
                        value={selectedAudience}
                        onChange={(e) => setSelectedAudience(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">Select an audience</option>
                        {isAudiencesLoading ? (
                          <option disabled>Loading audiences...</option>
                        ) : (
                          audiences?.audiences?.map((audience) => (
                            <option key={audience.id} value={audience.id}>
                              {audience.name}
                            </option>
                          )) || []
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => navigate("/integrations")}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={isLoading || !hasFormChanged || !isFormValid}
              className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-xs focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600 disabled:bg-indigo-300 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("common.updating")}
                </>
              ) : (
                t("common.save")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntegrationConfigPage;
